import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

export default function TermsAndConditions() {
  const [expandedSections, setExpandedSections] = useState({
    introduction: true,
    definitions: false,
    eligibility: false,
    accountRegistration: false,
    listingVehicles: false,
    bookingRentals: false,
    paymentTerms: false,
    cancellationPolicy: false,
    insurance: false,
    damageAccidents: false,
    dataProtection: false,
    liability: false,
    termination: false,
    disputes: false,
    modifications: false,
  });

  const toggleSection = (section) => {
    setExpandedSections({
      ...expandedSections,
      [section]: !expandedSections[section],
    });
  };

  // Component for section headers with toggle functionality
  const SectionHeader = ({ id, title }) => (
    <div
      className="flex justify-between items-center cursor-pointer py-4 border-b border-gray-200"
      onClick={() => toggleSection(id)}
    >
      <h2 className="text-xl font-bold text-gray-800">{title}</h2>
      {expandedSections[id] ? (
        <ChevronUp className="h-5 w-5 text-gray-600" />
      ) : (
        <ChevronDown className="h-5 w-5 text-gray-600" />
      )}
    </div>
  );

  // Component for section content
  const SectionContent = ({ id, children }) => (
    <div
      className={`py-4 text-gray-700 ${
        expandedSections[id] ? "block" : "hidden"
      }`}
    >
      {children}
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">RideMe</h1>
        <h2 className="text-2xl font-semibold text-gray-800">
          Terms and Conditions
        </h2>
        <p className="text-gray-600 mt-2">Last Updated: May 22, 2025</p>
      </div>

      <div className="space-y-4">
        {/* Introduction */}
        <div>
          <SectionHeader id="introduction" title="1. Introduction" />
          <SectionContent id="introduction">
            <p className="mb-3">
              Welcome to RideMe, a peer-to-peer car rental platform operating in
              Ghana. These Terms and Conditions govern your use of the RideMe
              platform, website, and services.
            </p>
            <p className="mb-3">
              By accessing or using our services, you agree to be bound by these
              Terms and Conditions. If you disagree with any part of these
              terms, you may not use our services.
            </p>
            <p>
              RideMe facilitates connections between vehicle owners and renters
              but is not a party to any transaction between users except as
              explicitly stated in these Terms.
            </p>
          </SectionContent>
        </div>

        {/* Definitions */}
        <div>
          <SectionHeader id="definitions" title="2. Definitions" />
          <SectionContent id="definitions">
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong>"RideMe,"</strong> "we," "us," or "our" refers to the
                RideMe platform.
              </li>
              <li>
                <strong>"User,"</strong> "you," or "your" refers to any person
                who accesses or uses the RideMe platform.
              </li>
              <li>
                <strong>"Owner"</strong> refers to a user who lists their
                vehicle for rent on RideMe.
              </li>
              <li>
                <strong>"Renter"</strong> refers to a user who rents a vehicle
                through RideMe.
              </li>
              <li>
                <strong>"Platform"</strong> refers to the RideMe website, mobile
                application, and related services.
              </li>
              <li>
                <strong>"Listing"</strong> refers to a vehicle offered for rent
                on the Platform.
              </li>
              <li>
                <strong>"Rental Agreement"</strong> refers to the agreement
                between an Owner and a Renter for the rental of a vehicle.
              </li>
            </ul>
          </SectionContent>
        </div>

        {/* Eligibility */}
        <div>
          <SectionHeader id="eligibility" title="3. Eligibility Requirements" />
          <SectionContent id="eligibility">
            <p className="mb-3">To use the RideMe platform, you must:</p>
            <ul className="list-disc pl-5 space-y-2 mb-3">
              <li>Be at least 18 years of age.</li>
              <li>Possess a valid driving license recognized in Ghana.</li>
              <li>Possess a valid Ghana Card for identity verification.</li>
              <li>Have a valid mobile phone number and email address.</li>
              <li>
                Meet any additional requirements that may be specified for
                particular vehicles.
              </li>
            </ul>
            <p className="mb-3">
              <strong>For Renters:</strong> You must have held a valid driving
              license for a minimum of one year and meet any additional
              requirements specified by vehicle owners or insurance providers.
            </p>
            <p>
              <strong>For Owners:</strong> You must have legal ownership or
              authorization to list the vehicle on the platform and ensure that
              the vehicle meets all legal requirements for road use in Ghana.
            </p>
          </SectionContent>
        </div>

        {/* Account Registration */}
        <div>
          <SectionHeader
            id="accountRegistration"
            title="4. Account Registration and Verification"
          />
          <SectionContent id="accountRegistration">
            <p className="mb-3">
              All users must create an account and complete the verification
              process to use RideMe services:
            </p>
            <ul className="list-disc pl-5 space-y-2 mb-3">
              <li>
                Provide accurate, current, and complete information during
                registration.
              </li>
              <li>
                Upload clear images of your valid driving license (front and
                back).
              </li>
              <li>Upload clear images of your Ghana Card (front and back).</li>
              <li>Verify your mobile phone number and email address.</li>
              <li>Complete any additional verification steps as required.</li>
            </ul>
            <p className="mb-3">
              RideMe reserves the right to reject account applications or
              suspend accounts if the provided information is inaccurate,
              outdated, or incomplete, or if user behavior violates these Terms.
            </p>
            <p>
              You are responsible for maintaining the confidentiality of your
              account credentials and for all activities that occur under your
              account.
            </p>
          </SectionContent>
        </div>

        {/* Listing Vehicles */}
        <div>
          <SectionHeader id="listingVehicles" title="5. Listing Vehicles" />
          <SectionContent id="listingVehicles">
            <p className="mb-3">Owners who list vehicles on RideMe must:</p>
            <ul className="list-disc pl-5 space-y-2 mb-3">
              <li>
                Provide accurate information about the vehicle, including make,
                model, year, features, condition, and any limitations.
              </li>
              <li>
                Upload clear, current photos of the vehicle from multiple angles
                (exterior and interior).
              </li>
              <li>Set reasonable rental rates and availability.</li>
              <li>
                Ensure the vehicle is legally registered in Ghana with current
                roadworthy certification.
              </li>
              <li>
                Ensure the vehicle is clean, well-maintained, and safe for
                operation.
              </li>
              <li>Disclose any known issues or defects with the vehicle.</li>
            </ul>
            <p>
              RideMe reserves the right to remove listings that violate these
              Terms or that we determine, in our sole discretion, are
              inappropriate for the platform.
            </p>
          </SectionContent>
        </div>

        {/* Booking Rentals */}
        <div>
          <SectionHeader id="bookingRentals" title="6. Booking Rentals" />
          <SectionContent id="bookingRentals">
            <p className="mb-3">
              When booking a vehicle through RideMe, Renters agree to:
            </p>
            <ul className="list-disc pl-5 space-y-2 mb-3">
              <li>
                Provide accurate information regarding rental dates, times, and
                intended use.
              </li>
              <li>
                Use the vehicle only for lawful purposes and in accordance with
                these Terms.
              </li>
              <li>
                Return the vehicle in the same condition as received, accounting
                for normal wear and tear.
              </li>
              <li>
                Return the vehicle with the same amount of fuel as when
                received, unless otherwise agreed with the Owner.
              </li>
              <li>
                Not smoke in the vehicle unless explicitly permitted by the
                Owner.
              </li>
              <li>
                Not use the vehicle for commercial purposes (such as taxi
                services) unless explicitly permitted by the Owner.
              </li>
            </ul>
            <p className="mb-3">
              Vehicle handover and return processes will be facilitated through
              the RideMe platform. Both parties must inspect the vehicle
              together and document its condition through the app at the start
              and end of the rental period.
            </p>
            <p>
              RideMe reserves the right to cancel bookings that we believe, in
              our sole discretion, may violate these Terms or present a risk to
              any user or vehicle.
            </p>
          </SectionContent>
        </div>

        {/* Payment Terms */}
        <div>
          <SectionHeader id="paymentTerms" title="7. Payment Terms" />
          <SectionContent id="paymentTerms">
            <p className="mb-3">RideMe payment terms include:</p>
            <ul className="list-disc pl-5 space-y-2 mb-3">
              <li>
                All payments must be processed through the RideMe platform.
                Direct payments between users are prohibited.
              </li>
              <li>
                Rental fees are set by Owners but may be subject to minimum and
                maximum limits set by RideMe.
              </li>
              <li>
                RideMe charges a service fee from both Owners and Renters for
                use of the platform.
              </li>
              <li>
                A security deposit may be required for each rental, to be held
                by RideMe during the rental period.
              </li>
              <li>
                Payments for additional charges (such as late returns, excess
                mileage, or cleaning fees) may be processed after the rental
                period ends.
              </li>
            </ul>
            <p className="mb-3">
              Renters authorize RideMe to charge their payment method for the
              total rental amount, including any applicable fees, taxes, and
              security deposit at the time of booking.
            </p>
            <p>
              Owners will receive payment within 3-5 business days after the
              completion of a successful rental, minus applicable RideMe service
              fees.
            </p>
          </SectionContent>
        </div>

        {/* Cancellation Policy */}
        <div>
          <SectionHeader
            id="cancellationPolicy"
            title="8. Cancellation and Refund Policy"
          />
          <SectionContent id="cancellationPolicy">
            <p className="mb-3">
              RideMe's cancellation and refund policy is as follows:
            </p>
            <ul className="list-disc pl-5 space-y-2 mb-3">
              <li>
                <strong>Full refund:</strong> Cancellations made before the
                Owner and Renter confirm vehicle pickup will receive a full
                refund minus processing fees.
              </li>
              <li>
                <strong>No refund:</strong> Once the Owner and Renter have
                confirmed pickup, no refund will be issued for cancellations.
              </li>
              <li>
                <strong>Owner cancellations:</strong> If an Owner cancels a
                confirmed booking, the Renter will receive a full refund, and
                the Owner may be subject to cancellation penalties.
              </li>
              <li>
                <strong>Changes to bookings:</strong> Modifications to existing
                bookings are subject to approval by both parties and may result
                in price adjustments.
              </li>
            </ul>
            <p className="mb-3">
              In exceptional circumstances (such as accidents, natural
              disasters, or civil unrest), RideMe may, at its discretion, offer
              full or partial refunds outside the standard policy.
            </p>
            <p>
              RideMe reserves the right to make the final decision on all
              cancellation disputes and refund requests.
            </p>
          </SectionContent>
        </div>

        {/* Insurance */}
        <div>
          <SectionHeader id="insurance" title="9. Insurance Coverage" />
          <SectionContent id="insurance">
            <p className="mb-3">
              RideMe provides comprehensive insurance coverage for all rentals
              through our partnership with a reputable Ghanaian insurance
              provider. Our coverage includes:
            </p>
            <ul className="list-disc pl-5 space-y-2 mb-3">
              <li>
                <strong>Basic Coverage (Included with all rentals):</strong>
                <ul className="list-disc pl-5 mt-1">
                  <li>Third-party liability protection up to GH₵8,000.00</li>
                  <li>
                    Collision damage coverage (per insurance terms and
                    conditions)
                  </li>
                  <li>Theft protection (per insurance terms and conditions)</li>
                </ul>
              </li>
              <li>
                <strong>Premium Coverage (Optional upgrade):</strong>
                <ul className="list-disc pl-5 mt-1">
                  <li>Third-party liability protection up to GH₵8,000.00</li>
                  <li>
                    Enhanced collision damage coverage (per insurance terms and
                    conditions)
                  </li>
                  <li>
                    Enhanced theft protection (per insurance terms and
                    conditions)
                  </li>
                  <li>Personal accident insurance for driver and passengers</li>
                </ul>
              </li>
            </ul>
            <p className="mb-3">Insurance coverage is only valid when:</p>
            <ul className="list-disc pl-5 space-y-2 mb-3">
              <li>
                The vehicle is operated by the registered Renter or an approved
                additional driver.
              </li>
              <li>
                All Terms and Conditions and local traffic laws are followed.
              </li>
              <li>
                The vehicle is not used for prohibited activities as outlined in
                these Terms.
              </li>
            </ul>
            <p>
              Details of insurance coverage will be provided during the booking
              process. The Renter is responsible for the deductible amount in
              case of damage or theft.
            </p>
          </SectionContent>
        </div>

        {/* Damage and Accidents */}
        <div>
          <SectionHeader
            id="damageAccidents"
            title="10. Vehicle Damage and Accidents"
          />
          <SectionContent id="damageAccidents">
            <p className="mb-3">In the event of vehicle damage or accidents:</p>
            <ul className="list-disc pl-5 space-y-2 mb-3">
              <li>
                <strong>Immediate reporting:</strong> The Renter must
                immediately report any accident, theft, or damage to the Police,
                the vehicle Owner, and RideMe.
              </li>
              <li>
                <strong>Documentation:</strong> The Renter must document the
                incident with photos and collect information from other parties
                involved (if applicable).
              </li>
              <li>
                <strong>Damage assessment:</strong> RideMe will facilitate an
                assessment of any damage through our claims process.
              </li>
              <li>
                <strong>Renter responsibility:</strong> The Renter is
                responsible for the insurance deductible and any damages not
                covered by insurance, including those resulting from:
                <ul className="list-disc pl-5 mt-1">
                  <li>Driving under the influence of alcohol or drugs</li>
                  <li>Reckless driving or violation of traffic laws</li>
                  <li>Unauthorized drivers operating the vehicle</li>
                  <li>Using the vehicle for prohibited purposes</li>
                  <li>
                    Damage to the interior that is not the result of a collision
                  </li>
                </ul>
              </li>
            </ul>
            <p>
              RideMe reserves the right to charge the Renter's payment method
              for verified damages or losses and to suspend or terminate
              accounts of users who repeatedly cause damage to vehicles.
            </p>
          </SectionContent>
        </div>

        {/* Data Protection */}
        <div>
          <SectionHeader
            id="dataProtection"
            title="11. Data Collection and Privacy"
          />
          <SectionContent id="dataProtection">
            <p className="mb-3">
              RideMe collects and processes personal data as necessary to
              provide our services. The data we collect includes:
            </p>
            <ul className="list-disc pl-5 space-y-2 mb-3">
              <li>
                Personal identification information (name, date of birth,
                contact details)
              </li>
              <li>
                Government-issued identification (driving license, Ghana Card)
              </li>
              <li>Payment information</li>
              <li>Vehicle information (for Owners)</li>
              <li>Location data during the rental period</li>
              <li>Communications between users and with our support team</li>
              <li>Usage data related to the platform</li>
            </ul>
            <p className="mb-3">
              By using RideMe, you consent to our collection and processing of
              your data in accordance with Ghanaian data protection laws. We
              implement appropriate security measures to protect your personal
              information.
            </p>
            <p className="mb-3">We may share your information with:</p>
            <ul className="list-disc pl-5 space-y-2 mb-3">
              <li>Other RideMe users as necessary to facilitate rentals</li>
              <li>
                Insurance partners for coverage verification and claims
                processing
              </li>
              <li>Payment processors to facilitate transactions</li>
              <li>Legal authorities when required by law</li>
            </ul>
            <p>
              You have the right to access, correct, and request deletion of
              your personal data, subject to our legal obligations to retain
              certain information.
            </p>
          </SectionContent>
        </div>

        {/* Liability */}
        <div>
          <SectionHeader id="liability" title="12. Limitation of Liability" />
          <SectionContent id="liability">
            <p className="mb-3">
              RideMe serves as a platform connecting vehicle Owners and Renters.
              While we strive to ensure a safe and reliable service, we cannot
              guarantee the condition of vehicles or the behavior of users.
            </p>
            <p className="mb-3">
              To the maximum extent permitted by law, RideMe shall not be liable
              for:
            </p>
            <ul className="list-disc pl-5 space-y-2 mb-3">
              <li>
                Any indirect, incidental, special, consequential, or punitive
                damages.
              </li>
              <li>Any loss of profits, revenue, data, or other intangibles.</li>
              <li>
                Personal injury or property damage resulting from your use of
                the platform or any vehicle rented through the platform.
              </li>
              <li>The actions or omissions of any users of our platform.</li>
              <li>Any content shared by users on the platform.</li>
              <li>Service interruptions or communication failures.</li>
            </ul>
            <p className="mb-3">
              Our total liability for any claim arising from or related to your
              use of the platform shall not exceed the amount paid by you to
              RideMe in the six months preceding the claim.
            </p>
            <p>
              This limitation of liability does not exclude or limit our
              liability where prohibited by applicable law.
            </p>
          </SectionContent>
        </div>

        {/* Termination */}
        <div>
          <SectionHeader
            id="termination"
            title="13. Account Suspension and Termination"
          />
          <SectionContent id="termination">
            <p className="mb-3">
              RideMe reserves the right to suspend or terminate your account and
              access to our services at any time for any reason, including but
              not limited to:
            </p>
            <ul className="list-disc pl-5 space-y-2 mb-3">
              <li>Violation of these Terms and Conditions</li>
              <li>
                Providing inaccurate, fraudulent, or incomplete information
              </li>
              <li>Engaging in illegal or harmful activities</li>
              <li>Repeated cancellations or no-shows</li>
              <li>Receiving consistent negative reviews or complaints</li>
              <li>Non-payment of fees or charges</li>
              <li>Violating the rights of other users</li>
            </ul>
            <p className="mb-3">
              You may terminate your account at any time by contacting our
              support team. However, you remain responsible for any outstanding
              obligations related to transactions conducted prior to account
              termination.
            </p>
            <p>
              Upon termination, your right to use the RideMe platform will
              immediately cease, and we may delete or deactivate your account
              and all related information and files.
            </p>
          </SectionContent>
        </div>

        {/* Disputes */}
        <div>
          <SectionHeader id="disputes" title="14. Dispute Resolution" />
          <SectionContent id="disputes">
            <p className="mb-3">
              In the event of a dispute between users, the parties should first
              attempt to resolve the issue directly through the platform's
              messaging system. If this is unsuccessful, RideMe's customer
              support team can be contacted to mediate.
            </p>
            <p className="mb-3">For disputes between users and RideMe:</p>
            <ul className="list-disc pl-5 space-y-2 mb-3">
              <li>
                We encourage you to contact our customer support team first to
                seek a resolution.
              </li>
              <li>
                If the dispute cannot be resolved through our support channels,
                it shall be resolved through arbitration in accordance with the
                laws of Ghana.
              </li>
              <li>
                The arbitration shall take place in Accra, Ghana, and shall be
                conducted in English.
              </li>
              <li>
                The decision of the arbitrator shall be final and binding on
                both parties.
              </li>
            </ul>
            <p>
              Any legal action arising from these Terms shall be governed by and
              construed in accordance with the laws of Ghana, without regard to
              its conflict of law provisions.
            </p>
          </SectionContent>
        </div>

        {/* Modifications */}
        <div>
          <SectionHeader
            id="modifications"
            title="15. Modifications to Terms"
          />
          <SectionContent id="modifications">
            <p className="mb-3">
              RideMe reserves the right to modify these Terms and Conditions at
              any time. When we make changes, we will:
            </p>
            <ul className="list-disc pl-5 space-y-2 mb-3">
              <li>Post the updated terms on the platform</li>
              <li>Update the "Last Updated" date at the top of these Terms</li>
              <li>
                Notify users of material changes via email or in-app
                notification
              </li>
            </ul>
            <p className="mb-3">
              Your continued use of RideMe after changes to the Terms
              constitutes your acceptance of the revised Terms. If you do not
              agree with the modified terms, you must stop using our services.
            </p>
            <p>
              We encourage users to periodically review the Terms to stay
              informed about our policies and practices.
            </p>
          </SectionContent>
        </div>

        {/* Contact Information */}
        <div className="py-4 border-t border-gray-200">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            Contact Information
          </h2>
          <p className="mb-3">
            If you have any questions or concerns regarding these Terms and
            Conditions, please contact us at:
          </p>
          <div className="pl-5">
            <p>
              <strong>The Bakery Technologies LTD</strong>
            </p>
            <p>Email: info@thebakery.tech</p>
            <p>Address: 11 Atomic Down Road, Dome-Kwabenya, Accra, Ghana</p>
          </div>
        </div>
      </div>

      <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-gray-700 text-center">
          By using the RideMe platform, you acknowledge that you have read,
          understood, and agree to be bound by these Terms and Conditions.
        </p>
      </div>
    </div>
  );
}
